import { useMemo } from "react";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { useCurrentUserHasFeatureFlag } from "hooks/featureFlags";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { RBAC_MODULE_KEYS } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

export const SCORING_INSTRUCTIONS = "Scoring Instructions";
export const EVALUATION_PERMISSIONS = "Evaluation Permissions";
export const SCHEDULE_OF_RATES = "Schedule of Rates";
export const QUOTE_TEMPLATES = "Quote Templates";
export const PANEL_MEMBER_DISCLAIMER = "Panel Member Disclaimer";
export const SUBMISSION_COMPLIANCE = "Submission Compliance";

const EVAL_PROCURER_TABS: TabMenuItem[] = [
	{ id: "submission-compliance", name: SUBMISSION_COMPLIANCE, showBadge: false, total: 0 },
	{ id: "evaluation-permissions", name: EVALUATION_PERMISSIONS, showBadge: false, total: 0 },
	{ id: "scoring-instructions", name: SCORING_INSTRUCTIONS, showBadge: false, total: 0 },
	{ id: "panel-member-disclaimer", name: PANEL_MEMBER_DISCLAIMER, showBadge: false, total: 0 },
];

const ALL_PROCURER_TABS: TabMenuItem[] = [
	{ id: "quote-templates", name: QUOTE_TEMPLATES, showBadge: false, total: 0 },
];

const SUPPLIER_TABS: TabMenuItem[] = [
	{ id: "schedule-of-rates", name: SCHEDULE_OF_RATES, showBadge: false, total: 0 },
];

/*
 * PRO-709 — every tab on this page used to hang off the single `settings`
 * module, so "can edit the company's quote templates" and "can edit the
 * evaluation scoring instructions" were the same grant. The backend split
 * `settings` per surface; this map is the FE half of that split.
 *
 * The four evaluation tabs deliberately share ONE key — they are one
 * administrative concern, not four.
 */
export const TAB_MODULE_KEY: Record<string, string> = {
	[QUOTE_TEMPLATES]: RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates,
	[SUBMISSION_COMPLIANCE]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
	[EVALUATION_PERMISSIONS]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
	[SCORING_INSTRUCTIONS]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
	[PANEL_MEMBER_DISCLAIMER]: RBAC_MODULE_KEYS.rfxSettingsEvaluation,
	[SCHEDULE_OF_RATES]: RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates,
};

export interface TabAccess {
	canEdit: boolean;
	canOpen: boolean;
}

export interface RfxSettingsTabs {
	/** The tabs this user can actually open, in display order. Empty ⇒ no access. */
	tabs: TabMenuItem[];
	/** Per-module open/edit capability, for the read-only banner. */
	accessByModule: Record<string, TabAccess>;
	isLoading: boolean;
}

/**
 * Which RFx Settings tabs the current user can open.
 *
 * SINGLE SOURCE OF TRUTH, and that is the whole point. The Settings menu and
 * the RFx Settings page used to answer two different questions about the same
 * door: the menu asked "can you view ANY of the three rfx_settings modules",
 * while the page asked "does the ACTIVE PROFILE offer a tab you can open, given
 * the evaluation feature flag". Those disagree in three ways —
 *
 *   1. profile: a procurer-profile user holding only `schedule_of_rates` (a
 *      supplier tab) passed the menu's check and found no procurer tab to open;
 *   2. feature flag: a user holding only `rfx_settings_evaluation` passed the
 *      menu's check, but with ENABLE_RFX_EVALUATION off the four evaluation
 *      tabs do not exist, so the page had nothing to show;
 *   3. admin bypass: the menu let `administers_company_rbac` through
 *      unconditionally; the page has never had such a bypass.
 *
 * — and every one of them put a menu item in front of a user that opened an
 * "Access denied" screen. Both callers now ask this hook instead, so the menu
 * entry exists exactly when the page has something behind it.
 *
 * Deliberately NOT given an admin bypass: adding one here would GRANT a legacy
 * company admin with no RBAC module grants access the page has never given
 * them. Removing the door from the menu is the honest half of the fix.
 */
export const useRfxSettingsTabs = (): RfxSettingsTabs => {
	const { isClientProfileActive } = useProfileContext();
	const hasEvaluation = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_RFX_EVALUATION);

	const quoteTemplates = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates);
	const evaluation = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsEvaluation);
	const scheduleOfRates = useModuleCan(RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates);

	const isLoading = quoteTemplates.isLoading || evaluation.isLoading || scheduleOfRates.isLoading;

	/*
	 * `canOpen` is view OR edit: the server sends both flags, and an edit grant
	 * without view is not a state the matrix produces — but treating edit as
	 * implying access means a capability payload that omits `view` still opens
	 * the tab rather than hiding a surface the user can write to.
	 */
	const accessByModule: Record<string, TabAccess> = useMemo(
		() => ({
			[RBAC_MODULE_KEYS.rfxSettingsQuoteTemplates]: {
				canOpen: quoteTemplates.canView || quoteTemplates.canEdit,
				canEdit: quoteTemplates.canEdit,
			},
			[RBAC_MODULE_KEYS.rfxSettingsEvaluation]: {
				canOpen: evaluation.canView || evaluation.canEdit,
				canEdit: evaluation.canEdit,
			},
			[RBAC_MODULE_KEYS.rfxSettingsScheduleOfRates]: {
				canOpen: scheduleOfRates.canView || scheduleOfRates.canEdit,
				canEdit: scheduleOfRates.canEdit,
			},
		}),
		[
			quoteTemplates.canView,
			quoteTemplates.canEdit,
			evaluation.canView,
			evaluation.canEdit,
			scheduleOfRates.canView,
			scheduleOfRates.canEdit,
		],
	);

	const profileTabs = useMemo(() => {
		if (!isClientProfileActive) {
			return SUPPLIER_TABS;
		}

		return hasEvaluation ? ALL_PROCURER_TABS.concat(EVAL_PROCURER_TABS) : ALL_PROCURER_TABS;
	}, [isClientProfileActive, hasEvaluation]);

	/*
	 * A tab the user holds no capability on is not rendered AND is not routable:
	 * the page's slug resolution only honours a slug that survives this filter,
	 * so typing /rfx-settings/quote-templates lands on the first permitted tab
	 * instead.
	 */
	const tabs = useMemo(
		() => profileTabs.filter((tab) => accessByModule[TAB_MODULE_KEY[tab.name]]?.canOpen === true),
		[profileTabs, accessByModule],
	);

	return { tabs, accessByModule, isLoading };
};
